import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * Select/Dropdown component for web
 *
 * Custom dropdown with search functionality
 */
const Select = ({
  label,
  placeholder = 'Select an option',
  value,
  onChange,
  options = [],
  error,
  disabled = false,
  required = false,
  searchable = false,
  multiple = false,
  className = '',
  id,
  name,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Generate a safe ID - handle cases where label might be a React element
  const selectId = id || name || (typeof label === 'string' ? `select-${label.replace(/\s+/g, '-').toLowerCase()}` : 'select');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input if searchable
      if (searchable && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, searchable]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      setSearchTerm('');
    }
  };

  const handleSelect = (option) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const isSelected = currentValues.some((v) => v === option.value);
      const newValue = isSelected
        ? currentValues.filter((v) => v !== option.value)
        : [...currentValues, option.value];
      onChange?.(newValue);
    } else {
      onChange?.(option.value);
      setIsOpen(false);
    }
    setSearchTerm('');
  };

  const filteredOptions = searchable && searchTerm
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const getDisplayValue = () => {
    if (multiple) {
      const selectedOptions = options.filter((opt) =>
        Array.isArray(value) && value.includes(opt.value)
      );
      return selectedOptions.length > 0
        ? selectedOptions.map((opt) => opt.label).join(', ')
        : placeholder;
    }

    const selectedOption = options.find((opt) => opt.value === value);
    return selectedOption ? selectedOption.label : placeholder;
  };

  const isSelected = (option) => {
    if (multiple) {
      return Array.isArray(value) && value.includes(option.value);
    }
    return value === option.value;
  };

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label htmlFor={selectId} className="label">
          {label}
          {required && <span className="text-error-600 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className={[
            'input text-left flex items-center justify-between',
            error && 'input-error',
            disabled && 'opacity-50 cursor-not-allowed',
            className,
          ].filter(Boolean).join(' ')}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          id={selectId}
          {...props}
        >
          <span className={!value || (Array.isArray(value) && value.length === 0) ? 'text-neutral-400' : ''}>
            {getDisplayValue()}
          </span>
          <svg
            className={`w-5 h-5 text-neutral-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-dropdown max-h-60 overflow-hidden">
            {searchable && (
              <div className="p-2 border-b border-neutral-200">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-neutral-200 rounded focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            )}

            <div className="overflow-y-auto max-h-52 scrollbar-thin" role="listbox">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleSelect(option)}
                    className={[
                      'w-full text-left px-4 py-2 text-sm hover:bg-neutral-50 transition-colors flex items-center justify-between',
                      isSelected(option) && 'bg-primary-50 text-primary-700 font-medium',
                    ].filter(Boolean).join(' ')}
                    role="option"
                    aria-selected={isSelected(option)}
                  >
                    <span>{option.label}</span>
                    {isSelected(option) && (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-sm text-center text-neutral-500">
                  No options found
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <p id={`${selectId}-error`} className="mt-1 text-sm text-error-600">
          {error}
        </p>
      )}
    </div>
  );
};

Select.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ),
  error: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  searchable: PropTypes.bool,
  multiple: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
};

export default Select;
